// IOS commands for infrastructure security (Domain 06): enable secret, local
// users, console/VTY lines, SSH, banners, AAA with RADIUS and TACACS+, numbered
// and named standard/extended ACLs (interface access-groups and VTY
// access-class) and switch port security.
// Same spec shape as ./cli.js: { pattern, kinds?, run(s, args) }.

import {
  ensureMgmt, mgmtOf, aaaOf, aclKind, aclEntries, aceText, aceShow, isNumberedStd, isNumberedExt, portOf,
  type7, type7Decode, secretText, secretOf, pwMatches, PSEC_DEFAULT, isMac, evalAcl, sshEnabled,
} from '../net/security.js';
import { isIp, macCisco } from '../net/addr.js';
import { ifStatus } from '../net/sim.js';
import { shortIf, ifSort } from './ifnames.js';

const R = (specs) => specs.map((x) => ({ ...x, kinds: ['router'] }));
const SW = (specs) => specs.map((x) => ({ ...x, kinds: ['switch'] }));
const INVALID = ["% Invalid input detected at '^' marker."];
const pad = (s, n) => String(s).padEnd(n);
const lpad = (s, n) => String(s).padStart(n);

export const SEC_SUFFIX = {
  'config-line': '(config-line)#', 'config-std-nacl': '(config-std-nacl)#', 'config-ext-nacl': '(config-ext-nacl)#',
  'config-radius-server': '(config-radius-server)#', 'config-server-tacacs': '(config-server-tacacs)#',
};
const exitTo = (s) => { s.state.mode = 'config'; s.state.line = s.state.acl = s.state.aaaSrv = null; return []; };
const EXITS = [{ pattern: 'exit', run: exitTo }];

// ------------------------------------------------------------ passwords / users
function tooShort(s, pw) {
  const n = mgmtOf(s.d).minLength || 0;
  return pw.length < n ? [`% Password too short - must be at least ${n} characters. Password configuration failed`] : null;
}
const store = (s, pw) => ({ pw, enc: ensureMgmt(s.d).encrypt });
function setEnable(s, kind, pw, algo) {
  const bad = tooShort(s, pw);
  if (bad) return bad;
  const m = ensureMgmt(s.d);
  if (kind === 'secret') {
    m.enableSecret = { pw, type: algo === 'scrypt' ? 9 : 5 };
    return [];
  }
  m.enablePassword = store(s, pw);
  return m.enableSecret && pwMatches(m.enableSecret, pw)
    ? ['The enable password you have chosen is the same as your enable secret.', 'This is not recommended.  Re-enter the enable password.']
    : [];
}
function setUser(s, name, pw, { priv = 1, secret = true, algo } = {}) {
  const bad = tooShort(s, pw);
  if (bad) return bad;
  const m = ensureMgmt(s.d);
  m.users[name] = { pw, priv: +priv, secret, type: secret ? (algo === 'scrypt' ? 9 : 5) : 0 };
  return [];
}

// ------------------------------------------------------------ SSH / RSA
function genRsa(s, bits) {
  const d = s.d;
  const m = ensureMgmt(d);
  if (/^(Router|Switch)$/.test(d.hostname)) return ['% Please define a hostname other than Router.'];
  if (!m.domainName) return ['% Please define a domain-name first.'];
  const b = +bits;
  if (b < 360 || b > 4096) return INVALID;
  const had = !!m.rsa;
  m.rsa = { bits: b, name: `${d.hostname}.${m.domainName}` };
  return [
    ...(had ? [`% You already have RSA keys defined named ${m.rsa.name}.`, '% They will be replaced.', ''] : []),
    `% Generating ${b} bit RSA keys, keys will be non-exportable...`,
    { text: `[OK] (elapsed time was ${b >= 2048 ? 3 : 1} seconds)`, delay: 600 }, '',
    ...(had ? [] : [`%SSH-5-ENABLED: SSH 1.99 has been enabled`]),
  ];
}

// ------------------------------------------------------------ banner (delimited text)
function banner(s, text) {
  const t = text.trim();
  const delim = t.startsWith('^C') ? '^C' : t[0];
  const rest = t.slice(delim.length);
  const end = rest.indexOf(delim);
  const m = ensureMgmt(s.d);
  if (end >= 0) { m.banner = rest.slice(0, end); return []; }
  // multi-line: collect until the delimiter
  s.state.bannerBuf = { delim, lines: rest ? [rest] : [] };
  const collect = (line) => {
    const i = line.indexOf(s.state.bannerBuf.delim);
    if (i >= 0) {
      if (i > 0) s.state.bannerBuf.lines.push(line.slice(0, i));
      ensureMgmt(s.d).banner = s.state.bannerBuf.lines.join('\n');
      s.state.bannerBuf = null;
      return [];
    }
    s.state.bannerBuf.lines.push(line);
    return { lines: [], ask: { prompt: '', answer: collect } };
  };
  return { lines: [`Enter TEXT message.  End with the character '${delim}'.`], ask: { prompt: '', answer: collect } };
}

// ------------------------------------------------------------ lines
function enterLine(s, kind, a, b) {
  if (kind === 'vty') {
    const max = s.d.kind === 'switch' ? 15 : 4;
    if (+a > max || (b != null && (+b > max || +b < +a))) return INVALID;
  }
  ensureMgmt(s.d);
  s.state.line = kind;
  s.state.mode = 'config-line';
  return [];
}
const L = (s) => ensureMgmt(s.d).lines[s.state.line];
const TRANSPORTS = [['ssh'], ['telnet'], ['all'], ['none'], ['telnet', 'ssh'], ['ssh', 'telnet']];
export const SEC_LINE = [
  ...EXITS,
  { pattern: 'end', run: (s) => s.endConfig() },
  { pattern: 'password <WORD>', run: (s, [p]) => { const bad = tooShort(s, p); if (bad) return bad; L(s).password = p; L(s).enc = ensureMgmt(s.d).encrypt; return []; } },
  { pattern: 'password 7 <WORD>', run: (s, [h]) => { L(s).password = type7Decode(h); L(s).enc = true; return []; } },
  { pattern: 'no password', run: (s) => { L(s).password = null; return []; } },
  { pattern: 'login', run: (s) => { L(s).login = 'line'; L(s).authList = null; return s.state.line === 'vty' && !L(s).password && !mgmtOf(s.d).aaa?.newModel ? [] : []; } },
  { pattern: 'no login', run: (s) => { L(s).login = 'none'; return []; } },
  { pattern: 'login local', run: (s) => { L(s).login = 'local'; return []; } },
  { pattern: 'login authentication <WORD>', run: (s, [n]) => { if (!mgmtOf(s.d).aaa?.newModel) return INVALID; L(s).authList = n === 'default' ? null : n; return []; } },
  { pattern: 'no login authentication', run: (s) => { L(s).authList = null; return []; } },
  { pattern: 'exec-timeout <0-35791>', run: (s, [m]) => { L(s).execTimeout = [+m, 0]; return []; } },
  { pattern: 'exec-timeout <0-35791> <0-2147483>', run: (s, [m, sec]) => { L(s).execTimeout = [+m, +sec]; return []; } },
  { pattern: 'no exec-timeout', run: (s) => { L(s).execTimeout = null; return []; } },
  { pattern: 'logging synchronous', run: (s) => { L(s).logSync = true; return []; } },
  { pattern: 'no logging synchronous', run: (s) => { L(s).logSync = false; return []; } },
  ...TRANSPORTS.map((t) => ({ pattern: `transport input ${t.join(' ')}`, run: (s) => { if (s.state.line !== 'vty') return INVALID; L(s).transport = t; return []; } })),
  { pattern: 'no transport input', run: (s) => { L(s).transport = ['all']; return []; } },
  { pattern: 'access-class <WORD> in', run: (s, [n]) => { if (s.state.line !== 'vty') return INVALID; L(s).accessClass = n; return []; } },
  { pattern: 'no access-class <WORD> in', run: (s) => { L(s).accessClass = null; return []; } },
];

// ------------------------------------------------------------ AAA
const METHOD_TOKENS = ['local', 'local-case', 'none', 'line', 'enable'];
function parseMethods(text) {
  const t = text.trim().split(/\s+/);
  const out = [];
  for (let i = 0; i < t.length; i++) {
    const w = t[i].toLowerCase();
    if (w === 'group') {
      const g = (t[++i] || '').toLowerCase();
      if (g !== 'radius' && g !== 'tacacs+') return null;
      out.push(`group ${g}`);
    } else if (METHOD_TOKENS.includes(w)) out.push(w);
    else return null;
  }
  return out.length && out.length <= 4 ? out : null;
}
const needAaa = (s) => (mgmtOf(s.d).aaa?.newModel ? null : INVALID);
function aaaServer(s, proto, name) {
  const a = aaaOf(s.d);
  a.servers[proto][name] ||= proto === 'radius' ? { ip: null, authPort: 1812, acctPort: 1813, key: null } : { ip: null, key: null };
  s.state.aaaSrv = { proto, name };
  s.state.mode = proto === 'radius' ? 'config-radius-server' : 'config-server-tacacs';
  return [];
}
const srv = (s) => aaaOf(s.d).servers[s.state.aaaSrv.proto][s.state.aaaSrv.name];
const SRV_COMMON = [
  ...EXITS,
  { pattern: 'end', run: (s) => s.endConfig() },
  { pattern: 'address ipv4 <A.B.C.D>', run: (s, [ip]) => { srv(s).ip = ip; return []; } },
  { pattern: 'no address ipv4', run: (s) => { srv(s).ip = null; return []; } },
  { pattern: 'key <WORD>', run: (s, [k]) => { srv(s).key = k; return []; } },
  { pattern: 'key 0 <WORD>', run: (s, [k]) => { srv(s).key = k; return []; } },
  { pattern: 'no key', run: (s) => { srv(s).key = null; return []; } },
];
export const SEC_RADIUS = [
  ...SRV_COMMON,
  { pattern: 'address ipv4 <A.B.C.D> auth-port <0-65535> acct-port <0-65535>', run: (s, [ip, a, b]) => { Object.assign(srv(s), { ip, authPort: +a, acctPort: +b }); return []; } },
];
export const SEC_TACACS = SRV_COMMON;

// ------------------------------------------------------------ ACL entries (generated patterns)
const ADDRS = [['any'], ['host', '<A.B.C.D>'], ['<A.B.C.D>', '<A.B.C.D>']];
const PORTOPS = [[], ['eq', '<PORT>'], ['gt', '<PORT>'], ['lt', '<PORT>'], ['range', '<PORT>', '<PORT>']];
const ICMP_OPTS = [[], ['echo'], ['echo-reply'], ['unreachable'], ['time-exceeded']];
const STD_ADDRS = [['any'], ['host', '<A.B.C.D>'], ['<A.B.C.D>'], ['<A.B.C.D>', '<A.B.C.D>']];

function takeAddr(form, args, i) {
  if (form[0] === 'any') return [{ base: '0.0.0.0', wild: '255.255.255.255' }, i];
  if (form[0] === 'host') return [{ base: args[i], wild: '0.0.0.0' }, i + 1];
  return [{ base: args[i], wild: form.length === 2 ? args[i + 1] : '0.0.0.0' }, i + (form.length === 2 ? 2 : 1)];
}
function takePort(proto, form, args, i) {
  if (!form.length) return [null, i];
  const a = portOf(proto, args[i]);
  if (form[0] === 'range') { const b = portOf(proto, args[i + 1]); return [a == null || b == null || b < a ? false : { op: 'range', a, b }, i + 2]; }
  return [a == null ? false : { op: form[0], a }, i + 1];
}
/** Build the entry described by one generated pattern. Returns entry or null (semantic error). */
function stdEntry(action, form, logged, args) {
  const [a] = takeAddr(form.length === 1 && form[0] === '<A.B.C.D>' ? ['<A.B.C.D>'] : form, args, 0);
  if (form[0] === '<A.B.C.D>' && form.length === 2 && !isIp(a.wild)) return null;
  // IOS stores the network address (host bits under the wildcard are cleared).
  return { kind: 'std', action, base: a.base, wild: a.wild, log: logged };
}
export function stdPatterns() {
  const out = [];
  for (const action of ['permit', 'deny']) {
    for (const form of STD_ADDRS) {
      for (const logged of [false, true]) out.push({ tail: [action, ...form, ...(logged ? ['log'] : [])].join(' '), make: (args) => stdEntry(action, form, logged, args) });
    }
  }
  return out;
}
export function extPatterns() {
  const out = [];
  for (const action of ['permit', 'deny']) {
    for (const proto of ['ip', 'icmp', 'tcp', 'udp']) {
      for (const sa of ADDRS) {
        for (const sp of proto === 'tcp' || proto === 'udp' ? PORTOPS : [[]]) {
          for (const da of ADDRS) {
            for (const dp of proto === 'tcp' || proto === 'udp' ? PORTOPS : [[]]) {
              for (const ic of proto === 'icmp' ? ICMP_OPTS : [[]]) {
                for (const est of proto === 'tcp' ? [false, true] : [false]) {
                  for (const logged of [false, true]) {
                    const tail = [action, proto, ...sa, ...sp, ...da, ...dp, ...ic, ...(est ? ['established'] : []), ...(logged ? ['log'] : [])].join(' ');
                    out.push({
                      tail,
                      make: (args) => {
                        let i = 0, src, dst, sport, dport;
                        [src, i] = takeAddr(sa, args, i);
                        [sport, i] = takePort(proto, sp, args, i);
                        [dst, i] = takeAddr(da, args, i);
                        [dport, i] = takePort(proto, dp, args, i);
                        if (sport === false || dport === false) return null;
                        return { kind: 'ext', action, proto, src, dst, ...(sport ? { sport } : {}), ...(dport ? { dport } : {}), ...(ic.length ? { icmpType: ic[0] } : {}), ...(est ? { established: true } : {}), log: logged };
                      },
                    });
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  return out;
}
const STD = stdPatterns();
const EXT = extPatterns();
const sameAce = (a, b) => JSON.stringify({ ...a, seq: 0, hits: 0 }) === JSON.stringify({ ...b, seq: 0, hits: 0 });
/** Add an entry; `seq` optional. Returns IOS output lines. */
export function addAce(d, name, entry, seq) {
  d.acls ||= {};
  const list = (d.acls[name] ||= []);
  aclEntries(d, name);
  if (seq != null && list.some((e) => e.seq === seq)) return ['% Duplicate sequence number'];
  if (list.some((e) => sameAce(e, entry))) return [];
  const s2 = seq ?? (list.length ? Math.max(...list.map((e) => e.seq)) + 10 : 10);
  list.push({ ...entry, seq: s2 });
  list.sort((a, b) => a.seq - b.seq);
  return [];
}
const badCmd = () => INVALID;
const numberedSpecs = (range, list, kind) => list.map((p) => ({
  pattern: `access-list ${range} ${p.tail}`,
  run: (s, [n, ...args]) => {
    const e = p.make(args);
    if (!e) return badCmd();
    return addAce(s.d, String(n), e);
  },
}));
const namedSpecs = (list, withSeq) => list.map((p) => ({
  pattern: `${withSeq ? '<1-2147483647> ' : ''}${p.tail}`,
  run: (s, args) => {
    const seq = withSeq ? +args[0] : null;
    const e = p.make(withSeq ? args.slice(1) : args);
    if (!e) return badCmd();
    return addAce(s.d, s.state.acl, e, seq);
  },
}));
function removeAce(s, list, args, p) {
  const e = p.make(args);
  const a = aclEntries(s.d, s.state.acl) || [];
  const k = a.findIndex((x) => e && sameAce(x, { ...e, seq: x.seq, hits: x.hits }));
  if (k >= 0) a.splice(k, 1);
  return [];
}
function enterAcl(s, type, name) {
  if (type === 'standard' && isNumberedExt(name)) return ['% Invalid access list name.'];
  if (type === 'extended' && isNumberedStd(name)) return ['% Invalid access list name.'];
  const d = s.d;
  d.acls ||= {}; d.aclType ||= {};
  if (d.acls[name] && aclKind(d, name) !== type) return [`% A named ${aclKind(d, name)} IP access list with this name already exists`];
  d.acls[name] ||= [];
  if (!isNumberedStd(name) && !isNumberedExt(name)) d.aclType[name] = type;
  s.state.acl = name;
  s.state.mode = type === 'standard' ? 'config-std-nacl' : 'config-ext-nacl';
  return [];
}
const NACL_COMMON = [
  ...EXITS,
  { pattern: 'end', run: (s) => s.endConfig() },
  { pattern: 'remark <LINE>', run: (s, [t]) => { s.d.acls[s.state.acl].push({ remark: t, seq: Math.max(0, ...(aclEntries(s.d, s.state.acl) || []).map((e) => e.seq || 0)) + 10 }); return []; } },
  { pattern: 'no <1-2147483647>', run: (s, [n]) => { const a = aclEntries(s.d, s.state.acl); const k = a.findIndex((e) => e.seq === +n); if (k >= 0) a.splice(k, 1); return []; } },
];
export const SEC_STD_NACL = [...NACL_COMMON, ...namedSpecs(STD, false), ...namedSpecs(STD, true), ...STD.map((p) => ({ pattern: `no ${p.tail}`, run: (s, args) => removeAce(s, STD, args, p) }))];
export const SEC_EXT_NACL = [...NACL_COMMON, ...namedSpecs(EXT, false), ...namedSpecs(EXT, true), ...EXT.map((p) => ({ pattern: `no ${p.tail}`, run: (s, args) => removeAce(s, EXT, args, p) }))];

// ------------------------------------------------------------ global configuration
export const SEC_CONFIG = [
  { pattern: 'enable secret <WORD>', run: (s, [p]) => setEnable(s, 'secret', p) },
  { pattern: 'enable secret 0 <WORD>', run: (s, [p]) => setEnable(s, 'secret', p) },
  { pattern: 'enable algorithm-type scrypt secret <WORD>', run: (s, [p]) => setEnable(s, 'secret', p, 'scrypt') },
  { pattern: 'no enable secret', run: (s) => { ensureMgmt(s.d).enableSecret = null; return []; } },
  { pattern: 'enable password <WORD>', run: (s, [p]) => setEnable(s, 'password', p) },
  { pattern: 'enable secret 5 <WORD>', run: (s, [h]) => { ensureMgmt(s.d).enableSecret = { pw: null, hash: h, type: 5 }; return []; } },
  { pattern: 'enable secret 9 <WORD>', run: (s, [h]) => { ensureMgmt(s.d).enableSecret = { pw: null, hash: h, type: 9 }; return []; } },
  { pattern: 'enable password 7 <WORD>', run: (s, [h]) => { ensureMgmt(s.d).enablePassword = { pw: type7Decode(h), enc: true }; return []; } },
  { pattern: 'no enable password', run: (s) => { ensureMgmt(s.d).enablePassword = null; return []; } },
  { pattern: 'service password-encryption', run: (s) => { ensureMgmt(s.d).encrypt = true; return []; } },
  { pattern: 'no service password-encryption', run: (s) => { ensureMgmt(s.d).encrypt = false; return []; } },
  { pattern: 'username <WORD> secret <WORD>', run: (s, [u, p]) => setUser(s, u, p) },
  { pattern: 'username <WORD> privilege <0-15> secret <WORD>', run: (s, [u, n, p]) => setUser(s, u, p, { priv: n }) },
  { pattern: 'username <WORD> algorithm-type scrypt secret <WORD>', run: (s, [u, p]) => setUser(s, u, p, { algo: 'scrypt' }) },
  { pattern: 'username <WORD> privilege <0-15> algorithm-type scrypt secret <WORD>', run: (s, [u, n, p]) => setUser(s, u, p, { priv: n, algo: 'scrypt' }) },
  { pattern: 'username <WORD> password <WORD>', run: (s, [u, p]) => setUser(s, u, p, { secret: false }) },
  { pattern: 'username <WORD> privilege <0-15> password <WORD>', run: (s, [u, n, p]) => setUser(s, u, p, { priv: n, secret: false }) },
  ...[5, 9].flatMap((t) => [
    { pattern: `username <WORD> secret ${t} <WORD>`, run: (s, [u, h]) => { ensureMgmt(s.d).users[u] = { pw: null, hash: h, priv: 1, secret: true, type: t }; return []; } },
    { pattern: `username <WORD> privilege <0-15> secret ${t} <WORD>`, run: (s, [u, n, h]) => { ensureMgmt(s.d).users[u] = { pw: null, hash: h, priv: +n, secret: true, type: t }; return []; } },
  ]),
  { pattern: 'username <WORD> password 7 <WORD>', run: (s, [u, h]) => { ensureMgmt(s.d).users[u] = { pw: type7Decode(h), priv: 1, secret: false, type: 0 }; return []; } },
  { pattern: 'username <WORD> privilege <0-15> password 7 <WORD>', run: (s, [u, n, h]) => { ensureMgmt(s.d).users[u] = { pw: type7Decode(h), priv: +n, secret: false, type: 0 }; return []; } },
  { pattern: 'no username <WORD>', run: (s, [u]) => { delete ensureMgmt(s.d).users[u]; return []; } },
  { pattern: 'ip domain-name <WORD>', run: (s, [n]) => { ensureMgmt(s.d).domainName = n.toLowerCase(); return []; } },
  { pattern: 'ip domain name <WORD>', run: (s, [n]) => { ensureMgmt(s.d).domainName = n.toLowerCase(); return []; } },
  { pattern: 'no ip domain-name', run: (s) => { ensureMgmt(s.d).domainName = null; return []; } },
  { pattern: 'no ip domain name', run: (s) => { ensureMgmt(s.d).domainName = null; return []; } },
  { pattern: 'crypto key generate rsa', run: (s) => {
    const m = ensureMgmt(s.d);
    if (/^(Router|Switch)$/.test(s.d.hostname)) return ['% Please define a hostname other than Router.'];
    if (!m.domainName) return ['% Please define a domain-name first.'];
    return { lines: [`The name for the keys will be: ${s.d.hostname}.${m.domainName}`, 'Choose the size of the key modulus in the range of 360 to 4096 for your', '  General Purpose Keys. Choosing a key modulus greater than 512 may take', '  a few minutes.', ''], ask: { prompt: 'How many bits in the modulus [512]: ', answer: (a) => genRsa(s, a.trim() || '512') } };
  } },
  { pattern: 'crypto key generate rsa modulus <360-4096>', run: (s, [b]) => genRsa(s, b) },
  { pattern: 'crypto key generate rsa general-keys modulus <360-4096>', run: (s, [b]) => genRsa(s, b) },
  { pattern: 'crypto key zeroize rsa', run: (s) => {
    if (!mgmtOf(s.d).rsa) return ['% No Signature RSA Keys found in configuration.'];
    return { lines: ['% All keys will be removed.', '% All router certs issued using these keys will also be removed.'], ask: { prompt: 'Do you really want to remove these keys? [yes/no]: ', answer: (a) => { if (!/^y/i.test(a.trim())) return []; ensureMgmt(s.d).rsa = null; return ['%SSH-5-DISABLED: SSH 1.99 has been disabled']; } } };
  } },
  { pattern: 'ip ssh version <1-2>', run: (s, [v]) => { ensureMgmt(s.d).ssh.version = +v; return []; } },
  { pattern: 'no ip ssh version', run: (s) => { ensureMgmt(s.d).ssh.version = null; return []; } },
  { pattern: 'ip ssh time-out <1-120>', run: (s, [t]) => { ensureMgmt(s.d).ssh.timeout = +t; return []; } },
  { pattern: 'ip ssh authentication-retries <0-5>', run: (s, [t]) => { ensureMgmt(s.d).ssh.retries = +t; return []; } },
  { pattern: 'banner motd <LINE>', run: (s, [t]) => banner(s, t) },
  { pattern: 'no banner motd', run: (s) => { ensureMgmt(s.d).banner = null; return []; } },
  { pattern: 'security passwords min-length <0-16>', run: (s, [n]) => { ensureMgmt(s.d).minLength = +n; return []; } },
  { pattern: 'no security passwords min-length', run: (s) => { ensureMgmt(s.d).minLength = 0; return []; } },
  { pattern: 'login on-failure log', run: (s) => { ensureMgmt(s.d).loginLog.failure = true; return []; } },
  { pattern: 'login on-success log', run: (s) => { ensureMgmt(s.d).loginLog.success = true; return []; } },
  { pattern: 'no login on-failure log', run: (s) => { ensureMgmt(s.d).loginLog.failure = false; return []; } },
  { pattern: 'no login on-success log', run: (s) => { ensureMgmt(s.d).loginLog.success = false; return []; } },
  { pattern: 'line console 0', run: (s) => enterLine(s, 'con') },
  { pattern: 'line vty <0-15> <0-15>', run: (s, [a, b]) => enterLine(s, 'vty', a, b) },
  { pattern: 'line vty <0-15>', run: (s, [a]) => enterLine(s, 'vty', a) },
  { pattern: 'ip http server', run: (s) => { ensureMgmt(s.d).httpServer = true; return []; } },
  { pattern: 'no ip http server', run: (s) => { ensureMgmt(s.d).httpServer = false; return []; } },
  // AAA
  { pattern: 'aaa new-model', run: (s) => { aaaOf(s.d).newModel = true; return []; } },
  { pattern: 'no aaa new-model', run: (s) => { if (s.d.mgmt?.aaa) s.d.mgmt.aaa.newModel = false; return []; } },
  { pattern: 'aaa authentication login <WORD> <LINE>', run: (s, [n, rest]) => { const bad = needAaa(s); if (bad) return bad; const m = parseMethods(rest); if (!m) return INVALID; aaaOf(s.d).authLogin[n] = m; return []; } },
  { pattern: 'no aaa authentication login <WORD>', run: (s, [n]) => { if (s.d.mgmt?.aaa) delete s.d.mgmt.aaa.authLogin[n]; return []; } },
  { pattern: 'aaa accounting exec default start-stop group radius', run: (s) => needAaa(s) || ((aaaOf(s.d).acctExec = 'group radius'), []) },
  { pattern: 'aaa accounting exec default start-stop group tacacs+', run: (s) => needAaa(s) || ((aaaOf(s.d).acctExec = 'group tacacs+'), []) },
  { pattern: 'no aaa accounting exec default', run: (s) => { if (s.d.mgmt?.aaa) s.d.mgmt.aaa.acctExec = null; return []; } },
  { pattern: 'radius server <WORD>', run: (s, [n]) => needAaa(s) || aaaServer(s, 'radius', n) },
  { pattern: 'no radius server <WORD>', run: (s, [n]) => { if (s.d.mgmt?.aaa) delete s.d.mgmt.aaa.servers.radius[n]; return []; } },
  { pattern: 'tacacs server <WORD>', run: (s, [n]) => needAaa(s) || aaaServer(s, 'tacacs', n) },
  { pattern: 'no tacacs server <WORD>', run: (s, [n]) => { if (s.d.mgmt?.aaa) delete s.d.mgmt.aaa.servers.tacacs[n]; return []; } },
  { pattern: 'ip radius source-interface <IF>', run: (s, [i]) => { aaaOf(s.d).srcIf = { ...(aaaOf(s.d).srcIf || {}), radius: s.ifn(i) }; return []; } },
  { pattern: 'no ip radius source-interface', run: (s) => { if (s.d.mgmt?.aaa?.srcIf) delete s.d.mgmt.aaa.srcIf.radius; return []; } },
  { pattern: 'ip tacacs source-interface <IF>', run: (s, [i]) => { aaaOf(s.d).srcIf = { ...(aaaOf(s.d).srcIf || {}), tacacs: s.ifn(i) }; return []; } },
  { pattern: 'no ip tacacs source-interface', run: (s) => { if (s.d.mgmt?.aaa?.srcIf) delete s.d.mgmt.aaa.srcIf.tacacs; return []; } },
  // ACLs (numbered and named)
  ...numberedSpecs('<1-99>', STD, 'standard'),
  ...numberedSpecs('<1300-1999>', STD, 'standard'),
  ...numberedSpecs('<100-199>', EXT, 'extended'),
  ...numberedSpecs('<2000-2699>', EXT, 'extended'),
  { pattern: 'access-list <1-99> remark <LINE>', run: (s, [n, t]) => { s.d.acls ||= {}; (s.d.acls[n] ||= []).push({ remark: t }); aclEntries(s.d, n); return []; } },
  { pattern: 'access-list <100-199> remark <LINE>', run: (s, [n, t]) => { s.d.acls ||= {}; (s.d.acls[n] ||= []).push({ remark: t }); aclEntries(s.d, n); return []; } },
  { pattern: 'no access-list <1-99>', run: (s, [n]) => { if (s.d.acls) delete s.d.acls[n]; return []; } },
  { pattern: 'no access-list <100-199>', run: (s, [n]) => { if (s.d.acls) delete s.d.acls[n]; return []; } },
  { pattern: 'ip access-list standard <WORD>', run: (s, [n]) => enterAcl(s, 'standard', n) },
  { pattern: 'ip access-list extended <WORD>', run: (s, [n]) => enterAcl(s, 'extended', n) },
  { pattern: 'no ip access-list standard <WORD>', run: (s, [n]) => { if (s.d.acls) delete s.d.acls[n]; if (s.d.aclType) delete s.d.aclType[n]; return []; } },
  { pattern: 'no ip access-list extended <WORD>', run: (s, [n]) => { if (s.d.acls) delete s.d.acls[n]; if (s.d.aclType) delete s.d.aclType[n]; return []; } },
];

// ------------------------------------------------------------ interface configuration
const accessGroup = (dir) => (s, [n]) => s.eachIf((i) => { i.accessGroup = { ...(i.accessGroup || {}), [dir]: n }; });
const noAccessGroup = (dir) => (s) => s.eachIf((i) => { if (i.accessGroup) i.accessGroup[dir] = null; });
export const SEC_IF_ROUTER = R([
  { pattern: 'ip access-group <WORD> in', run: accessGroup('in') },
  { pattern: 'ip access-group <WORD> out', run: accessGroup('out') },
  { pattern: 'no ip access-group <WORD> in', run: noAccessGroup('in') },
  { pattern: 'no ip access-group <WORD> out', run: noAccessGroup('out') },
  { pattern: 'no ip access-group in', run: noAccessGroup('in') },
  { pattern: 'no ip access-group out', run: noAccessGroup('out') },
]);
function psecEach(s, fn) {
  for (const n of s.state.ifs) {
    const i = s.d.interfaces[n];
    if (i.mode === 'trunk' || !i.explicitAccess) return [`Command rejected: ${n} is a dynamic port.`];
  }
  return s.eachIf((i) => { i.psec ||= PSEC_DEFAULT(); fn(i.psec, i); });
}
export const SEC_IF_SWITCH = SW([
  { pattern: 'switchport port-security', run: (s) => psecEach(s, (p) => { p.enabled = true; }) },
  { pattern: 'no switchport port-security', run: (s) => s.eachIf((i) => { if (i.psec) { i.psec.enabled = false; i.psec.macs = i.psec.macs.filter((m) => m.type !== 'dynamic'); } }) },
  { pattern: 'switchport port-security maximum <1-132>', run: (s, [n]) => psecEach(s, (p) => { p.max = +n; }) },
  { pattern: 'no switchport port-security maximum', run: (s) => s.eachIf((i) => { if (i.psec) i.psec.max = 1; }) },
  ...['protect', 'restrict', 'shutdown'].map((v) => ({ pattern: `switchport port-security violation ${v}`, run: (s) => psecEach(s, (p) => { p.violation = v; }) })),
  { pattern: 'no switchport port-security violation', run: (s) => s.eachIf((i) => { if (i.psec) i.psec.violation = 'shutdown'; }) },
  { pattern: 'switchport port-security mac-address sticky', run: (s) => psecEach(s, (p) => { p.sticky = true; p.macs.forEach((m) => { if (m.type === 'dynamic') m.type = 'sticky'; }); }) },
  { pattern: 'no switchport port-security mac-address sticky', run: (s) => s.eachIf((i) => { if (i.psec) { i.psec.sticky = false; i.psec.macs = i.psec.macs.filter((m) => m.type !== 'sticky'); } }) },
  { pattern: 'switchport port-security mac-address <WORD>', run: (s, [m]) => (isMac(m) ? psecEach(s, (p, i) => { if (!p.macs.some((x) => x.mac === m.toLowerCase())) p.macs.push({ mac: m.toLowerCase(), type: 'static', vlan: i.accessVlan }); }) : INVALID) },
  { pattern: 'switchport port-security mac-address sticky <WORD>', run: (s, [m]) => (isMac(m) ? psecEach(s, (p, i) => { p.sticky = true; if (!p.macs.some((x) => x.mac === m.toLowerCase())) p.macs.push({ mac: m.toLowerCase(), type: 'sticky', vlan: i.accessVlan }); }) : INVALID) },
  { pattern: 'no switchport port-security mac-address <WORD>', run: (s, [m]) => s.eachIf((i) => { if (i.psec) i.psec.macs = i.psec.macs.filter((x) => x.mac !== m.toLowerCase()); }) },
  { pattern: 'no switchport port-security mac-address sticky <WORD>', run: (s, [m]) => s.eachIf((i) => { if (i.psec) i.psec.macs = i.psec.macs.filter((x) => x.mac !== m.toLowerCase()); }) },
]);

// ------------------------------------------------------------ show / exec
function showAcls(d, only) {
  const out = [];
  for (const name of Object.keys(d.acls || {}).sort((a, b) => (/^\d+$/.test(a) && /^\d+$/.test(b) ? +a - +b : a < b ? -1 : 1))) {
    if (only && name !== only) continue;
    const kind = aclKind(d, name);
    out.push(`${kind === 'extended' ? 'Extended' : 'Standard'} IP access list ${name}`);
    for (const e of aclEntries(d, name)) { const l = aceShow(e); if (l) out.push(l); }
  }
  return out;
}
function showIpInterface(net, id, n) {
  const d = net.devices[id];
  const i = d.interfaces[n];
  const st = ifStatus(net, id, n);
  const ag = i.accessGroup || {};
  const pfx = i.ip ? i.ip.mask.split('.').reduce((a, o) => a + (+o).toString(2).split('').filter((b) => b === '1').length, 0) : null;
  return [
    `${n} is ${st.status}, line protocol is ${st.protocol}`,
    ...(i.ip ? [`  Internet address is ${i.ip.ip}/${pfx}`, '  Broadcast address is 255.255.255.255', '  Address determined by setup command', `  MTU is ${Number(i.ipMtu) || 1500} bytes`] : ['  Internet protocol processing disabled']),
    ...(i.ip ? [
      `  Helper address is ${i.helper || 'not set'}`, '  Directed broadcast forwarding is disabled',
      `  Outgoing access list is ${ag.out || 'not set'}`, `  Inbound  access list is ${ag.in || 'not set'}`,
      '  Proxy ARP is enabled', '  Local Proxy ARP is disabled', '  Security level is default', '  Split horizon is enabled',
      '  ICMP redirects are always sent', '  ICMP unreachables are always sent', '  ICMP mask replies are never sent',
      '  IP fast switching is enabled', '  IP CEF switching is enabled', `  IP Flow switching is disabled`,
      `  Network address translation is ${i.nat ? `enabled, interface in domain ${i.nat}` : 'disabled'}`,
    ] : []),
  ];
}
function showSsh(d) {
  const m = mgmtOf(d);
  if (!m.rsa) return [`SSH Disabled - version ${m.ssh.version === 2 ? '2.0' : '1.99'}`, '%Please create RSA keys to enable SSH (and of atleast 768 bits for SSH v2).', `Authentication methods:publickey,keyboard-interactive,password`, `Authentication timeout: ${m.ssh.timeout} secs; Authentication retries: ${m.ssh.retries}`];
  return [`SSH Enabled - version ${m.ssh.version === 2 ? '2.0' : '1.99'}`, 'Authentication methods:publickey,keyboard-interactive,password', 'Encryption Algorithms:aes128-ctr,aes192-ctr,aes256-ctr',
    `Authentication timeout: ${m.ssh.timeout} secs; Authentication retries: ${m.ssh.retries}`, 'Minimum expected Diffie Hellman key size : 1024 bits', `IOS Keys in SECSH format(ssh-rsa, base64 encoded): ${m.rsa.name}`, `Modulus Size : ${m.rsa.bits} bits`];
}
function showAaa(d) {
  const a = mgmtOf(d).aaa;
  if (!a?.newModel) return ['% AAA is not enabled (no "aaa new-model").'];
  const out = [];
  let id = 1;
  for (const proto of ['radius', 'tacacs']) {
    for (const [name, s] of Object.entries(a.servers[proto])) {
      const st = a.stats?.[`${proto}|${name}`] || { requests: 0, accept: 0, reject: 0, timeouts: 0 };
      out.push(proto === 'radius'
        ? `RADIUS: id ${id}, priority ${id}, host ${s.ip || 'not set'}, auth-port ${s.authPort}, acct-port ${s.acctPort}, hostname ${name}`
        : `TACACS+: id ${id}, priority ${id}, host ${s.ip || 'not set'}, auth-port 49, acct-port 49, hostname ${name}`,
      `     State: current ${st.requests && st.timeouts === st.requests ? 'DEAD' : 'UP'}, duration 0s, previous duration 0s`,
      `     Dead: total time 0s, count ${st.timeouts ? 1 : 0}`,
      `     Authen: request ${st.requests}, timeouts ${st.timeouts}, failover 0, retransmission ${st.timeouts}`,
      `             Response: accept ${st.accept}, reject ${st.reject}, challenge 0`,
      `             Response: unexpected 0, server error 0, incorrect 0, time 4ms`, '');
      id++;
    }
  }
  return out.length ? out : ['% No AAA servers configured.'];
}
const STATUS_TEXT = (net, id, n) => {
  const i = net.devices[id].interfaces[n];
  if (i.errDisabled) return 'Secure-shutdown';
  return ifStatus(net, id, n).connected ? 'Secure-up' : 'Secure-down';
};
function psecIfShow(net, id, n) {
  const i = net.devices[id].interfaces[n];
  const p = i.psec || PSEC_DEFAULT();
  return [
    `Port Security              : ${p.enabled ? 'Enabled' : 'Disabled'}`,
    `Port Status                : ${p.enabled ? STATUS_TEXT(net, id, n) : 'Secure-down'}`,
    `Violation Mode             : ${p.violation[0].toUpperCase()}${p.violation.slice(1)}`,
    'Aging Time                 : 0 mins', 'Aging Type                 : Absolute', 'SecureStatic Address Aging : Disabled',
    `Maximum MAC Addresses      : ${p.max}`, `Total MAC Addresses        : ${p.macs.length}`,
    `Configured MAC Addresses   : ${p.macs.filter((m) => m.type === 'static').length}`, `Sticky MAC Addresses       : ${p.macs.filter((m) => m.type === 'sticky').length}`,
    `Last Source Address:Vlan   : ${p.last || '0000.0000.0000:0'}`, `Security Violation Count   : ${p.count}`,
  ];
}
function psecTable(d) {
  const rows = ['Secure Port  MaxSecureAddr  CurrentAddr  SecurityViolation  Security Action', '                (Count)       (Count)          (Count)', '---------------------------------------------------------------------------'];
  for (const n of Object.keys(d.interfaces).sort(ifSort)) {
    const p = d.interfaces[n].psec;
    if (!p?.enabled) continue;
    rows.push(`${lpad(shortIf(n), 11)}${lpad(p.max, 15)}${lpad(p.macs.length, 13)}${lpad(p.count, 19)}${' '.repeat(9)}${p.violation[0].toUpperCase()}${p.violation.slice(1)}`);
  }
  rows.push('---------------------------------------------------------------------------', 'Total Addresses in System (excluding one mac per port)     : 0', 'Max Addresses limit in System (excluding one mac per port) : 4096');
  return rows;
}
function psecAddr(d) {
  const rows = ['               Secure Mac Address Table', '-----------------------------------------------------------------------------', 'Vlan    Mac Address       Type                          Ports   Remaining Age', '                                                                   (mins)', '----    -----------       ----                          -----   -------------'];
  for (const n of Object.keys(d.interfaces).sort(ifSort)) {
    const p = d.interfaces[n].psec;
    if (!p?.enabled) continue;
    for (const m of p.macs) rows.push(`${lpad(m.vlan || d.interfaces[n].accessVlan, 4)}    ${pad(m.mac, 18)}${pad({ sticky: 'SecureSticky', dynamic: 'SecureDynamic', static: 'SecureConfigured' }[m.type], 30)}${pad(shortIf(n), 8)}${lpad('-', 8)}`);
  }
  rows.push('-----------------------------------------------------------------------------', 'Total Addresses in System (excluding one mac per port)     : 0', 'Max Addresses limit in System (excluding one mac per port) : 4096');
  return rows;
}
function errDisabledStatus(net, id) {
  const d = net.devices[id];
  const rows = ['Port      Name               Status       Reason               Err-disabled Vlans'];
  for (const n of Object.keys(d.interfaces).sort(ifSort)) {
    const i = d.interfaces[n];
    if (i.errDisabled) rows.push(`${pad(shortIf(n), 10)}${pad(i.description.slice(0, 18), 19)}${pad('err-disabled', 13)}${i.errDisabled}`);
  }
  return rows;
}
function rsaKey(d) {
  const m = mgmtOf(d);
  if (!m.rsa) return ['% Key pair was not generated (no RSA keys).'];
  const hex = (seed, n) => { let h = 0x9e3779b9, o = ''; for (let k = 0; o.length < n; k++) { h = Math.imul(h ^ (seed.charCodeAt(k % seed.length) + k), 2654435761) >>> 0; o += h.toString(16).toUpperCase().padStart(8, '0'); } return o.slice(0, n); };
  const body = hex(m.rsa.name + m.rsa.bits, Math.round(m.rsa.bits / 4));
  const lines = body.match(/.{1,64}/g).map((x) => `  ${x.match(/.{1,8}/g).join(' ')}`);
  return [`% Key pair was generated at: 09:14:07 PHT Oct 7 2026`, `Key name: ${m.rsa.name}`, `Key type: RSA KEYS`, ` Storage Device: not specified`, ` Usage: General Purpose Key`, ` Key is not exportable.`, ` Key Data:`, ...lines];
}
const showLogs = (d) => {
  const L = d.logging || {};
  return [`Syslog logging: enabled (0 messages dropped, 0 flushes, 0 overruns, xml disabled, filtering disabled)`, '',
    `    Console logging: level debugging, ${(d.logs || []).length} messages logged, xml disabled,`, '                     filtering disabled',
    `    Buffer logging:  level debugging, ${(d.logs || []).length} messages logged, xml disabled,`, '                    filtering disabled', '',
    `    Trap logging: level ${L.trap || 'informational'}, ${(L.hosts || []).length} message lines logged`, ...(L.hosts || []).map((h) => `        Logging to ${h}  (udp port 514, audit disabled,`),
    '', `Log Buffer (8192 bytes):`, '', ...(d.logs || []).map((e) => `${e.t}: ${e.text}`)];
};
export const SEC_SHOW = [
  { pattern: 'show access-lists', run: (s) => showAcls(s.d) },
  { pattern: 'show access-lists <WORD>', run: (s, [n]) => showAcls(s.d, n) },
  { pattern: 'show ip access-lists', run: (s) => showAcls(s.d) },
  { pattern: 'show ip access-lists <WORD>', run: (s, [n]) => showAcls(s.d, n) },
  { pattern: 'show ip ssh', run: (s) => showSsh(s.d) },
  { pattern: 'show ssh', run: () => ['%No SSHv2 server connections running.'] },
  { pattern: 'show crypto key mypubkey rsa', run: (s) => rsaKey(s.d) },
  { pattern: 'show aaa servers', run: (s) => showAaa(s.d) },
  { pattern: 'show users', run: (s) => ['    Line       User       Host(s)              Idle       Location', `*  0 con 0     ${pad(s.state.loginUser || '', 11)}idle                 00:00:00`, '', '  Interface    User               Mode         Idle     Peer Address'] },
  ...R([{ pattern: 'show ip interface <IF>', run: (s, [i]) => showIpInterface(s.net, s.id, s.ifn(i)) }]),
  ...SW([
    { pattern: 'show port-security', run: (s) => psecTable(s.d) },
    { pattern: 'show port-security interface <IF>', run: (s, [i]) => psecIfShow(s.net, s.id, s.ifn(i)) },
    { pattern: 'show port-security address', run: (s) => psecAddr(s.d) },
    { pattern: 'show interfaces status err-disabled', run: (s) => errDisabledStatus(s.net, s.id) },
    { pattern: 'show logging', run: (s) => showLogs(s.d) },
  ]),
];
export const SEC_USER_SHOW = [
  { pattern: 'show privilege', run: (s) => [`Current privilege level is ${s.state.mode === 'user' ? 1 : 15}`] },
];
export const SEC_PRIV = [
  { pattern: 'test aaa group radius <WORD> <WORD> legacy', run: (s, [u, p]) => s.testAaa('radius', u, p) },
  { pattern: 'test aaa group tacacs+ <WORD> <WORD> legacy', run: (s, [u, p]) => s.testAaa('tacacs', u, p) },
  { pattern: 'clear access-list counters', run: (s) => { for (const n of Object.keys(s.d.acls || {})) for (const e of s.d.acls[n]) e.hits = 0; return []; } },
  { pattern: 'clear access-list counters <WORD>', run: (s, [n]) => { for (const e of s.d.acls?.[n] || []) e.hits = 0; return []; } },
];

// ------------------------------------------------------------ running-config
const pwText = (pw, enc) => (enc ? `7 ${type7(pw)}` : pw);
/** Global lines right after hostname. */
export function secGlobalLines(d) {
  const m = d.mgmt;
  if (!m) return [];
  const out = [];
  if (m.minLength) out.push(`security passwords min-length ${m.minLength}`);
  if (m.enableSecret) out.push(`enable secret ${secretOf(m.enableSecret)}`);
  if (m.enablePassword) out.push(`enable password ${pwText(m.enablePassword.pw, m.encrypt || m.enablePassword.enc)}`);
  if (out.length) out.push('!');
  const a = m.aaa;
  if (a?.newModel) {
    out.push('aaa new-model', '!');
    for (const [n, list] of Object.entries(a.authLogin)) out.push(`aaa authentication login ${n} ${list.join(' ')}`);
    if (a.acctExec) out.push(`aaa accounting exec default start-stop ${a.acctExec}`);
    out.push('!', 'aaa session-id common', '!');
  }
  if (m.domainName) out.push(`ip domain name ${m.domainName}`);
  const users = Object.entries(m.users);
  if (users.length) {
    for (const [u, x] of users) out.push(`username ${u}${x.priv !== 1 ? ` privilege ${x.priv}` : ''} ${x.secret ? `secret ${secretOf(x)}` : `password ${pwText(x.pw, m.encrypt)}`}`);
    out.push('!');
  }
  if (m.loginLog.failure) out.push('login on-failure log');
  if (m.loginLog.success) out.push('login on-success log');
  if (m.ssh.version) out.push(`ip ssh version ${m.ssh.version}`);
  if (m.ssh.timeout !== 120) out.push(`ip ssh time-out ${m.ssh.timeout}`);
  if (m.ssh.retries !== 3) out.push(`ip ssh authentication-retries ${m.ssh.retries}`);
  if (m.loginLog.failure || m.loginLog.success || m.ssh.version || m.domainName) out.push('!');
  return out;
}
/** Named ACLs, AAA servers and http lines near the end of the configuration. */
export function secTailLines(d) {
  const out = [];
  for (const [n, list] of Object.entries(d.acls || {})) {
    if (/^\d+$/.test(n)) continue;
    out.push(`ip access-list ${aclKind(d, n)} ${n}`);
    for (const e of aclEntries(d, n)) out.push(` ${e.remark != null ? `remark ${e.remark}` : aceText(e)}`);
  }
  if (out.length) out.push('!');
  const m = d.mgmt;
  if (m?.aaa) {
    for (const [name, s] of Object.entries(m.aaa.servers.radius)) out.push(`radius server ${name}`, ...(s.ip ? [` address ipv4 ${s.ip} auth-port ${s.authPort} acct-port ${s.acctPort}`] : []), ...(s.key ? [` key ${s.key}`] : []), '!');
    for (const [name, s] of Object.entries(m.aaa.servers.tacacs)) out.push(`tacacs server ${name}`, ...(s.ip ? [` address ipv4 ${s.ip}`] : []), ...(s.key ? [` key ${s.key}`] : []), '!');
    if (m.aaa.srcIf?.radius) out.push(`ip radius source-interface ${m.aaa.srcIf.radius}`);
    if (m.aaa.srcIf?.tacacs) out.push(`ip tacacs source-interface ${m.aaa.srcIf.tacacs}`);
  }
  if (m) out.push(m.httpServer ? 'ip http server' : 'no ip http server', '!');
  return out;
}
export function secNumberedAclLines(d) {
  const out = [];
  for (const [n, list] of Object.entries(d.acls || {})) {
    if (!/^\d+$/.test(n)) continue;
    for (const e of aclEntries(d, n)) out.push(`access-list ${n} ${e.remark != null ? `remark ${e.remark}` : aceText(e)}`);
  }
  return out;
}
export function secIfLines(d, n, i) {
  const out = [];
  if (i.accessGroup?.in) out.push(` ip access-group ${i.accessGroup.in} in`);
  if (i.accessGroup?.out) out.push(` ip access-group ${i.accessGroup.out} out`);
  const p = i.psec;
  if (p && (p.enabled || p.macs.length || p.max !== 1 || p.violation !== 'shutdown' || p.sticky)) {
    if (p.max !== 1) out.push(` switchport port-security maximum ${p.max}`);
    if (p.violation !== 'shutdown') out.push(` switchport port-security violation ${p.violation}`);
    if (p.sticky) out.push(' switchport port-security mac-address sticky');
    for (const m of p.macs) if (m.type !== 'dynamic') out.push(` switchport port-security mac-address ${m.type === 'sticky' ? 'sticky ' : ''}${m.mac}`);
    if (p.enabled) out.push(' switchport port-security');
  }
  return out;
}
/** banner + line blocks (replaces the legacy fixed text when security is configured). */
export function secLineBlocks(d) {
  const m = d.mgmt;
  if (!m) return null;
  const out = [];
  if (m.banner != null) out.push(`banner motd ^C${m.banner}^C`, '!');
  const block = (title, L, kind) => {
    out.push(title);
    if (L.accessClass && kind === 'vty') out.push(` access-class ${L.accessClass} in`);
    if (L.execTimeout) out.push(` exec-timeout ${L.execTimeout[0]} ${L.execTimeout[1]}`);
    if (L.password) out.push(` password ${pwText(L.password, m.encrypt || L.enc)}`);
    if (L.logSync) out.push(' logging synchronous');
    if (L.authList) out.push(` login authentication ${L.authList}`);
    else if (L.login === 'local') out.push(' login local');
    else if (L.login === 'line') out.push(' login');
    if (kind === 'vty' && !(L.transport.length === 1 && L.transport[0] === 'all')) out.push(` transport input ${L.transport.join(' ')}`);
  };
  block('line con 0', m.lines.con, 'con');
  out.push('line aux 0');
  block('line vty 0 4', m.lines.vty, 'vty');
  if (d.kind === 'switch') block('line vty 5 15', m.lines.vty, 'vty');
  out.push('!');
  return out;
}
export { sshEnabled, evalAcl, macCisco };
